import { ScanTypeCatalog, ScanTypeSlug } from './scanTypes.ts';
import { SampleDto, SampleCreateDto, SampleSetDto, SampleSetCreateDto,
  setSampleParameterValuesByName, createSet, updateSet, deleteSet, getSampleBySlug,
  createSample, updateSample, deleteSample, } from './api/api.ts';
import { ChangeSet, EditQueueEntry, UndoHistory, UndoHistoryEntry } from "./undoHistory.ts";
import { ResponseWrapper } from './api/basicApi.ts';
import { Uid, parseAndGetPrecision, sortWithNumberParsing, generateUniqueNames } from "./utils.tsx";

// Classes to represent samples, sets of samples, and undo/redo history for changes to them.


// This is a standard data structure used to indicate the selection of
// a single field, or parameter, in a SampleConfiguration object.
// For example the sample editing table has columns that refer to fields like "Name", "Description", etc,
// but also has columns for parameters.  This structure can be used to indicate one of those columns.
export enum SampleConfigurationField { Id, Name, Description, ScanType, Parameter };
export interface SampleConfigurationFieldSelection {
  field: SampleConfigurationField;
  // If "field" is set to "Parameter", this value becomes relevant.
  parameter: Uid | null
};


// Represents the configuration for one sample
export class SampleConfiguration {

  // A unique identifier generated on the server.
  id: Uid;
  // A unique slug generated on the server, used for API references.
  slug: string;
  // Unique identifier of the set this config belongs to.
  setSlug: string;
  // Denormalized from the Set record.
  esafSlug: string;
  // Intended to be short and unique, but this is not strictly enforced.
  name: string;
  // Meant to be longer than the name.  Can be blank.
  description: string;
  scanStatus: string;
  scanTypeSlug: ScanTypeSlug;
  // Key/value parameter set. Parameter values may be given for Parameters that
  // are not in the currently chosen ScanType.
  // This is not strictly enforced, so old inapplicable values can be preserved
  // in case a previous ScanType is re-selected.
  parameterValuesByName: Map<string, any>
  idOfMostRecentEditor: number;
  // A cached value used as a reference by e.g. findRelevantParameters.
  // This can stay undefined until legitimate ScanType information is available.
  scanTypesReference!: ScanTypeCatalog;

	constructor (p: SampleDto, v: {[key: string]: any}) {
		this.id = p.id;
    this.slug = p.slug;
		this.setSlug = p.slug_set;
    this.esafSlug = p.slug_esaf;
		this.name = p.name;
		this.description = p.description || "";
    this.scanStatus = p.scan_status;
		this.scanTypeSlug = p.slug_scan_type;
		this.parameterValuesByName = new Map(v ? Object.entries(v) : []);
    this.idOfMostRecentEditor = p.id_most_recent_editor;
	}

  setScanTypes(scanTypesReference: ScanTypeCatalog) {
    this.scanTypesReference = scanTypesReference;
  }


  clone():SampleConfiguration {
    const asDtos = this.asDtos();
    return new SampleConfiguration(asDtos[0], asDtos[1]);
  }


  async writeToServer():Promise<ResponseWrapper<boolean>> {
    const updateResult = await updateSample({
      slug: this.slug,
      name: this.name,
      description: this.description,
      slug_scan_type: this.scanTypeSlug,
      scan_status: this.scanStatus,
    });
    if (!updateResult.success) {
      return { success: false, message: updateResult.message };
    }

    // Time for a mini-essay.  Bear with me.
    // Over the course of editing, a user may have changed the Scan Type of a sample,
    // which may have added new parameters to the sample.
    // These ParameterValue structures are created with Ids of 0, and represent un-created records on the server.
    // Multiple entries in the undo/redo history may have appeared since the last sync with the server,
    // meaning multiple copies of a sample with ParameterValues that don't have Ids.
    // So, a ParameterValue we encounter here may have an Id of 0 but may potentially already have a record
    // created for it during some previous catch-up sync.  (Not likely, but possibly.)
    // To deal with this sort of chaos we use a bulk update/create API call that avoids it:

    const result = await setSampleParameterValuesByName(this.slug, {
      create_parameters_if_missing: false,
      add_parameters_to_scan_type_if_missing: false,
      allow_parameters_not_in_scan_type: true,
      remove_other_values: false,
      values: Object.fromEntries(Array.from(this.parameterValuesByName.entries())),
    });

    if (!result.success) {
      return { success: false, message: result.message };
    }
    return { success: true, response: true };
  }


  asDtos():[SampleDto, {[key: string]: any}] {
    // We want to make sure we're returning copies, not references.
    const values_by_name = Object.fromEntries(Array.from(this.parameterValuesByName.entries()));
    //const values_by_name: {[key: string]: any} = {};
    //this.parameterValuesByName.forEach((v, k) => {
    //  values_by_name.set(k, v);
    //});
    return [{
      id: this.id,
      name: this.name,
      slug: this.slug,
      description: this.description,
      slug_set: this.setSlug,
      slug_esaf: this.esafSlug,
      slug_scan_type: this.scanTypeSlug,
      scan_status: this.scanStatus,
      id_most_recent_editor: this.idOfMostRecentEditor,
      date_last_modified: new Date().toISOString()
    }, values_by_name];
  }
}


export class SampleConfigurationSet {
  id: Uid;
  slug: string;
  // Intended to be short and unique within an ESAF, but this is not strictly enforced.
  name: string;
  // Can remain empty
  description: string;
  esafSlug: string;
  scanStatus: string;
  idOfMostRecentEditor: number;
  dateLastModified: string;
  samplesBySlug: Map<string, SampleConfiguration>;
  // An array of identifiers of all parameters used
  // by the ScanTypes of all current SampleConfigurations.
  relevantParameters: Array<Uid>;
  // Undo/redo history tracker
  history: UndoHistory;
  // A cached value used as a reference by e.g. findRelevantParameters.
  // This can stay undefined until legitimate ScanType information is available.
  scanTypesReference!: ScanTypeCatalog;

  constructor(p: SampleSetDto) {
    this.id = p.id;
    this.slug = p.slug;
    this.name = p.name;
    this.description = p.description;
    this.esafSlug = p.slug_esaf;
    this.scanStatus = p.scan_status;
    this.idOfMostRecentEditor = p.id_most_recent_editor;
    this.dateLastModified = p.date_last_modified;
    this.samplesBySlug = new Map();
    this.relevantParameters = [];
    this.history = new UndoHistory();
  }

  setScanTypes(scanTypesReference: ScanTypeCatalog) {
    this.scanTypesReference = scanTypesReference;
    this.samplesBySlug.forEach((s) => s.setScanTypes(scanTypesReference));
    this.findRelevantParameters();
  }

  getSortedSamples() {
    const sortedSamples = this.all().sort((a, b) => { return sortWithNumberParsing(a.name, b.name)});
    return sortedSamples;
  }


  // Look through all the current Samples in this Set for Parameters,
  // and gather all the unique Parameter Ids into a list, ordered by their
  // universal "ordering" value.
  findRelevantParameters() {
    let workingSet: Set<Uid> = new Set();

    // We default to sorting Sample records by name.
    const samples = this.all();

    const typesBySlug = this.scanTypesReference.typesBySlug;
    samples.forEach((v) => {
      if (!typesBySlug.has(v.scanTypeSlug)) { return; } // Unlikely but might as well check.
      const t = typesBySlug.get(v.scanTypeSlug)!;
      t.parameterIds.forEach((id) => {
        workingSet.add(id);
      });
    });
    const parametersById = this.scanTypesReference.parametersById;
    this.relevantParameters = Array.from(workingSet).sort((a, b) => {
      const paramA = parametersById.get(a)!;
      const paramB = parametersById.get(b)!;
      return paramA.order - paramB.order;
    });
  }


  // Generate and return an array of SampleConfiguration objects suitable for
  // sending to the server for the creation of new configurations in this set.
  // This includes creating default parameter values that respect uniqueness constraints
  // relative to the existing configurations.
  generateNewConfigurationsWithDefaults(quantity: number, scanTypeSlug: ScanTypeSlug, suggestedName?: string, suggestedDescription?: string): { sample: SampleCreateDto, values: Map<string, string> }[] {

    const scanTypesBySlug = this.scanTypesReference.typesBySlug;
    // For use inside anonymous functions where we can't reference "this".
    const parametersById = this.scanTypesReference.parametersById;
    const samplesBySlug = this.samplesBySlug;

    if (!scanTypesBySlug.has(scanTypeSlug)) { return []; }
    const scanType = scanTypesBySlug.get(scanTypeSlug)!;

    const uniqueNames = this.generateUniqueNames(suggestedName ?? "Sample", quantity);

    const parametersThatNeedUniqueValues = 
      scanType.parameterIds.map(
        (id) => parametersById.get(id)!
      ).filter((p) => {
        return p?.enforceUniqueInSet
      });

    var uniqueParameterValues: Map<Uid, string[]> = new Map();

    // For each parameter that needs a unique value, create a list of acceptable values,
    // long enough to meet our needs.
    parametersThatNeedUniqueValues.forEach((p) => {
      var values = [];
      var index = 0;

      var defaultString = p.standardDefault ?? "0";
      const settings = scanType.parameterSettingsById.get(p.id);
      if (settings) {
        defaultString = settings.default ?? defaultString;
      }
      var [defaultValue, defaultPrecision] = parseAndGetPrecision(defaultString, "0");
      const [intervalValue, intervalPrecision] = parseAndGetPrecision((p.autoGenerateInterval ?? 1).toString(), "1");
      var precision = Math.max(defaultPrecision, intervalPrecision);

      // We're starting with the default, but if there are any existing configs,
      // we check their parameters for a value and, if it's higher than what we already have,
      // we use that (plus the interval) as the starting point.
      var value = defaultValue;
      samplesBySlug.forEach((v, k) => {
        const pv = v.parameterValuesByName.get(p.name);
        if (!pv) { return; }
        var [currentValue, currentPrecision] = parseAndGetPrecision(pv.value ?? "", "");
        if (!isNaN(currentValue)) {
          value = Math.max(currentValue+intervalValue, value);
          precision = Math.max(precision, currentPrecision);
        }
      });

      // Create an array of values, separated by the interval
      while (index < quantity) {
        // We convert this value to a string with a fixed number of decimal places,
        // with the precision being that of either the default, the interval, or any existing values, whichever is highest.
        values.push((value + (index * intervalValue)).toFixed(precision));
        index++;
      }
      uniqueParameterValues.set(p.id, values);
    });

    var newSampleDtos: { sample: SampleCreateDto, values: Map<string, string> }[] = [];
    var index = 0;
    while (index < quantity) {

      // Make a set of parameters for the chosen ScanType, with default or blank values.
      const values:Map<string, string> = new Map();
      scanType.parameterIds.forEach((id) => {
        const p = parametersById.get(id)!;
        var defaultValue = p.standardDefault ?? "";
        const settings = scanType.parameterSettingsById.get(p.id);
        if (settings) {
          defaultValue = settings.default ?? defaultValue;
        }
        values.set(p.name, defaultValue);
      });
      // For all the parameters that need unique defaults, select one from our prepared arrays.
      parametersThatNeedUniqueValues.forEach((p) => {
        const uniqueValues = uniqueParameterValues.get(p.id)!;
        values.set(p.name, uniqueValues[index]);
      });

      newSampleDtos.push({
        sample: {
          name: uniqueNames[index],
          description: suggestedDescription || "",
          slug_set: this.slug,
          slug_scan_type: scanType.slug
        },
        values: values
      });
      index++;
    }
    return newSampleDtos;
  }


  generateUniqueNames(suggestedName: string, quantity?: number, startIndex?: number | null): string[] {
    let existingNames: string[] = [];
    this.samplesBySlug.forEach((v, k) => { existingNames.push(v.name) });

    return generateUniqueNames(existingNames, suggestedName, quantity, startIndex);
  }


  all() {
    return Array.from(this.samplesBySlug.values());
  }


  // Add the given SampleConfiguration objects,
  // making an undo history entry along the way.
  // This is only called by the "add" sample dialog,
  // and it generates the relevant records on the server before calling.
  addWithHistory(input: SampleConfiguration[]) {
    const h = new UndoHistoryEntry();
    const currentSet = this.samplesBySlug;

    // Every given SampleConfiguration is a new change in forward history
    input.forEach((i) => h.forwardAdd(i));
    // We used to do a "backwardDelete" as well, but now we don't remove
    // new Samples when we go back in the undo history because we consider
    // deletion to be a permanent operation.  The new samples remain even
    // if we undo changes to the old ones.
    // Event construction is complete.
    this.history.do(h);

    // Now that we've updated undo/redo history, write the changes.
    input.forEach((i) => currentSet.set(i.slug, i));
    const st = this.scanTypesReference;
    input.forEach((i) => i.setScanTypes(st));
    // The set of relevant parameters may have changed.
    this.findRelevantParameters();
  }


  // Replace the given SampleConfiguration objects,
  // making an undo history entry along the way.
  replaceWithHistory(input: SampleConfiguration[]) {
    const h = new UndoHistoryEntry();
    const currentSet = this.samplesBySlug;

    // Every given SampleConfiguration is a new change in forward history
    input.forEach((i) => h.forwardAdd(i));
    // Every SampleConfiguration in the existing set that's replaced
    // should be preserved and restored if we go backward in history (undo)
    const verifiedExisting = input.filter((i) => currentSet.has(i.slug));
    verifiedExisting.forEach((i) => h.backwardAdd(currentSet.get(i.slug)!));
    // Event construction is complete.
    this.history.do(h);

    // Now that we've updated undo/redo history, write the changes.
    input.forEach((i) => currentSet.set(i.slug, i));
    const st = this.scanTypesReference;
    input.forEach((i) => i.setScanTypes(st));
    // The set of relevant parameters may have changed.
    this.findRelevantParameters();
  }


  // Adds the given SampleConfiguration objects to the set without doing any checking.
  // Used to build up an initial state.
  add(input: SampleConfiguration[]) {
    input.forEach((i) => this.samplesBySlug.set(i.slug, i));
    const st = this.scanTypesReference;
    input.forEach((i) => i.setScanTypes(st));
    // The set of relevant parameters may have changed.
    this.findRelevantParameters();
  }

  // Removes SampleConfigurations without doing any checking.
  // Note that if there is pending redo or edit history to catch up with,
  // this will have unpredictable effects.
  remove(slugs: string[]) {
    slugs.forEach((slug) => { this.samplesBySlug.delete(slug); });
    // The set of relevant parameters may have changed.
    this.findRelevantParameters();
  }

	undo(): ChangeSet | null {
    const edit = this.history.undo();
    if (!edit) { return null; }

    const currentSet = this.samplesBySlug;
    edit.additions.forEach((a) => {
      const s = a as SampleConfiguration;
      currentSet.set(s.slug, s)
    });
    // We used to handle removing anything in a "deletions" field as well,
    // but now we consider deletions permanent and a manual prcoess only,
    // so we don't make them part of undo history.

    // The set of relevant parameters may have changed.
    this.findRelevantParameters();
    return edit;
	}

  redo(): ChangeSet | null {
    const edit = this.history.redo();
    if (!edit) { return null; }

    const currentSet = this.samplesBySlug;
    edit.additions.forEach((a) => {
      const s = a as SampleConfiguration;
      currentSet.set(s.slug, s)
    });

    // The set of relevant parameters may have changed.
    this.findRelevantParameters();
    return edit;
	}

  canRedo(): boolean {
    return this.history.canRedo();
  }

  canUndo(): boolean {
    return this.history.canUndo();
  }

  getPendingChanges(): EditQueueEntry | null {
    return this.history.getPendingChanges();
  }

  // Pulls items off the beginning of the edit queue
  // up to and including the item with the given index,
  // or just clears the whole queue if that index isn't present.
  catchUpToEdit(index:number) {
    return this.history.catchUpToEdit(index);
  }

  // This writes the current state of the SampleSet record to the server,
  // but does not update any of the Sample or ParameterValue records it controls.
  async writeToServer():Promise<ResponseWrapper<boolean>> {
    const updateResult = await updateSet({
      slug: this.slug,
      name: this.name,
      description: this.description,
      scan_status: this.scanStatus,
    });
    if (!updateResult.success) {
      return { success: false, message: updateResult.message };
    }
    return { success: true, response: true };
  }

  async catchUpEdits(): Promise<ResponseWrapper<boolean>> {
    const thisSet = this;
    const changes = this.getPendingChanges();
    let errors:string[] = [];
    if (changes) {
      const saveCalls = changes.changes.additions.map((e) => {
        const c = e as SampleConfiguration;
        return c.writeToServer();
      });

      Promise.all(saveCalls).then((responses) => {
        if (responses.every((r) => r.success)) {
          thisSet.catchUpToEdit(changes.index);
        } else {
          responses.forEach((r) => {
            if (!r.success && r.message) {
              errors.push(r.message);
            }
          });
        }
      });
      if (errors.length > 0) {
        return { success: false, message: "Could not catch up edits before deleteion: " + errors.join(', ') };
      }
    }
    return { success: true, response: true };
  }


  async deleteSamplesAndSelfFromServer(): Promise<ResponseWrapper<boolean>> {
    // Samples have 'protected' status in the database, so we need to delete them before deleting the Set.
    const deleteSampleResult = await this.deleteSamplesFromServer(Array.from(this.samplesBySlug.keys()));
    if (!deleteSampleResult.success) {
      return deleteSampleResult;
    }
    const deleteSetResult = await deleteSet(this.slug);
    if (!deleteSetResult.success) {
      return { success: false, message: "Failed to delete the set itself." };
    }
    return { success: true, response: true };
  }


  async deleteSamplesFromServer(slugs: string[]): Promise<ResponseWrapper<boolean>> {
    const catchUpresult = await this.catchUpEdits();
    if (!catchUpresult.success) {
      return catchUpresult;
    }
    const deleteSampleCalls = slugs.map((s) => deleteSample(s));
    const deleteSampleResults = await Promise.all(deleteSampleCalls);
    if (deleteSampleResults.some((r) => !r.success)) {
      return { success: false, message: "Failed to delete one or more samples in the set." };
    }
    slugs.forEach((slug) => this.samplesBySlug.delete(slug));
    this.history.reset();
    this.findRelevantParameters();
    return { success: true, response: true };
  }
}


// A set of sets of SampleConfigurations, and functions to manage them.
export class SampleSets {
  esafSlug: string;
  name: string;
  setsBySlug: Map<string, SampleConfigurationSet>;
  scanTypesCache?: ScanTypeCatalog;

  constructor(name: string, esafSlug: string) {
    this.esafSlug = esafSlug;
    this.name = name;
    this.setsBySlug = new Map();
  }

  // Calls setScanTypes for all ConfigurationSets, passing the given ScanTypeCatalog along.
  setScanTypes(scanTypesCache: ScanTypeCatalog) {
    this.scanTypesCache = scanTypesCache;
    this.setsBySlug.forEach((t) => t.setScanTypes(scanTypesCache));
  }

  // Adds the given SampleConfigurationSet objects to the set without doing any checking.
  // Used to build up an initial state.
  add(input: SampleConfigurationSet[]) {
    const t = this.scanTypesCache;
    input.forEach((c) => {
      if (t) { c.setScanTypes(t); }
      this.setsBySlug.set(c.slug, c);
    });
  }

  // Removes a SampleSet without doing any checking! Dangerous!
  remove(slugs: string[]) {
    slugs.forEach((slug) => { this.setsBySlug.delete(slug); });
  }

  getBySlug(slug: string): SampleConfigurationSet | undefined {
    return this.setsBySlug.get(slug);
  }

  generateUniqueNames(suggestedName: string, quantity?: number, startIndex?: number | null): string[] {
    let existingNames: string[] = [];
    this.setsBySlug.forEach((v) => { existingNames.push(v.name) });
    return generateUniqueNames(existingNames, suggestedName, quantity, startIndex);
  }

  all() {
    return Array.from(this.setsBySlug.values());
  }

  getSortedSets() {
    const sortedSets = this.all().sort((a, b) => { return sortWithNumberParsing(a.name, b.name)});
    return sortedSets;
  }


  async moveSamplesToSet(samples: SampleConfiguration[], destinationSetSlug: string): Promise<ResponseWrapper<boolean>> {
    const destinationSet = this.setsBySlug.get(destinationSetSlug);
    if (!destinationSet) {
      return { success: false, message: "Destination set not found." };
    }

    const currentSampleNames = new Set<string>(destinationSet.all().map(s => s.name));
    const samplesWithConflictingNames = samples.filter((s) => currentSampleNames.has(s.name));
    // If we need to add indexes, we should add them in an orderly fashion
    const conflictingSamplesSorted = samplesWithConflictingNames.sort((a, b) => sortWithNumberParsing(a.name, b.name));

    var failedToResolveConflicts = false;
    conflictingSamplesSorted.forEach((sample) => {
      const newNames = generateUniqueNames(Array.from(currentSampleNames), sample.name, 1);
      if (newNames.length === 0) {
        failedToResolveConflicts = true;
        console.error("Failed to generate a unique name for a sample during move operation. Name: " + name);
      } else {
        sample.name = newNames[0];
        currentSampleNames.add(sample.name);
      }
    });
    if (failedToResolveConflicts) {
      return { success: false, message: "Failed to resolve name conflicts for one or more samples during move operation." };
    }

    // Need to be able to set slug in update action
    const moveCalls = samples.map((s) => {
      return updateSample({
        slug: s.slug,
        name: s.name,
        description: s.description,
        slug_scan_type: s.scanTypeSlug,
        scan_status: s.scanStatus,
        slug_set: destinationSetSlug
      });
    });
    const moveResults = await Promise.all(moveCalls);
    if (moveResults.some((r) => !r.success)) {
      return { success: false, message: "Failed to move one or more samples." };
    }

    // Update the local state to reflect the move.
    samples.forEach((s) => {
      this.setsBySlug.get(s.setSlug)?.samplesBySlug.delete(s.slug);
      s.setSlug = destinationSetSlug;
      destinationSet.samplesBySlug.set(s.slug, s);
    });
    // The set of relevant parameters may have changed for both sets.
    destinationSet.findRelevantParameters();
    const sourceSetSlugs = new Set(samples.map((s) => s.setSlug));
    sourceSetSlugs.forEach((slug) => {
      const set = this.setsBySlug.get(slug);
      if (set) {
        set.findRelevantParameters();
      }
    });

    return { success: true, response: true };
  }


  // Deletes all the Samples in a Set from the server, deletes the Set, and removes it from this instance.
  async deleteSetFromServerAndRemove(slug: string): Promise<ResponseWrapper<boolean>> {
    const setToDelete = this.setsBySlug.get(slug);
    if (!setToDelete) { return { success: false, message: "Set not found." }; }
    const result = await setToDelete.deleteSamplesAndSelfFromServer();
    if (result.success) {
      this.remove([slug]);
    } else {
      console.error("Failed to delete set: " + result.message);
    }
    return result;
  }
}


// Create a new Set record on the server
export async function createNewSet(dto: SampleSetCreateDto): Promise<ResponseWrapper<SampleConfigurationSet>> {
  const result = await createSet(dto);
  if (!result.success) {
    return { success: false, message: result.message };
  }
  const newSet = new SampleConfigurationSet(result.response!);
  return { success: true, response: newSet };
}


// Create a new Sample record on the server and return the instantiated SampleConfiguration if successful.
export async function createNewSampleAndParameterValues(sample: SampleCreateDto, values: Map<string, any>): Promise<ResponseWrapper<SampleConfiguration>> {
  let result = await createSample(sample);
  if (!result.success) {
    return { success: false, message: result.message };
  }
  const newSlug = result.response!.slug;
  const valuesAsObject = Object.fromEntries(Array.from(values.entries()));

  result = await setSampleParameterValuesByName(newSlug, {
    create_parameters_if_missing: false,
    add_parameters_to_scan_type_if_missing: false,
    allow_parameters_not_in_scan_type: true,
    remove_other_values: false,
    values: valuesAsObject,
  });

  if (!result.success) {
    return { success: false, message: result.message };
  }

  // We need to fetch the full record from the server again after we've created
  // the Parameters, to get their values and slugs in context.
  const sampleFromServer = await getSampleBySlug(newSlug);
  if (!sampleFromServer.success) {
    return { success: false, message: sampleFromServer.message };
  }

  const newConfig = new SampleConfiguration(sampleFromServer.response!, valuesAsObject);
  return { success: true, response: newConfig };
}
